import { Button, Divider, HStack, Text, useToast, VStack } from '@chakra-ui/react'
import parse from 'html-react-parser'
import React, { FunctionComponent, useCallback, useState } from 'react'
import Card from '../../common/components/Card'
import { ScrollableContainer } from '../../components/shared-components/ScrollableContainer'
import useApi from '../../common/utils/api/useApi'
import {
  PbsConcise,
  PbsRestriction,
  ProductDetails,
  ProductSearchClient,
} from './ScriptForm/ProductSearchContextSlice'
import { convertRestrictionType, describePbsProgram } from './utilities'
import { useAppSelector } from '../../app/typedReduxHooks'
import VisuallyDisabledButton from '../../components/shared-components/VisuallyDisabledButton'
import { RootState } from '../../app/store'

interface AuthorityDetailsProps {
  details?: ProductDetails
  handleSelect?: (pbsData: PbsConcise, restriction?: PbsRestriction) => any
  isOnlyPrivateMedicationAllowed: boolean
}

const AuthorityDetailsPanel: FunctionComponent<AuthorityDetailsProps> = ({
  details,
  handleSelect,
  isOnlyPrivateMedicationAllowed,
}) => {
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isAbleToSelectS8Medicine = organizationSettings?.enableS8Medicine
  const isS8Medicine = !!details?.poisonClassification?.poisonClasses?.includes('S8')

  if (!details?.pbs?.length) {
    return null
  }

  return (
    <Card p="2" variant="light" overflow="auto">
      <VStack spacing="4" alignItems="stretch">
        {details.pbs.map((pbsData: PbsConcise) => (
          <PbsCard
            key={pbsData.pbsCode}
            pbsData={pbsData}
            handleSelect={handleSelect}
            isDisableS8Medicine={!isAbleToSelectS8Medicine && isS8Medicine}
            isOnlyPrivateMedicationAllowed={isOnlyPrivateMedicationAllowed}
          />
        ))}
      </VStack>
    </Card>
  )
}

const PbsCard = ({
  pbsData,
  handleSelect,
  isDisableS8Medicine,
  isOnlyPrivateMedicationAllowed,
}: {
  pbsData: PbsConcise
  handleSelect?: (pbsData: PbsConcise, restriction?: PbsRestriction) => any
  isOnlyPrivateMedicationAllowed: boolean
  isDisableS8Medicine: boolean
}) => {
  const { trigger: getFullPbsData, response: fullPbsData } = useApi(
    ProductSearchClient.getFullPbsData
  )
  const toast = useToast()
  const selectedPatient = useAppSelector(
    ({ patientDetail }: RootState) => patientDetail.selectedPatient
  )
  const handleReadMore = (pbsCode: string) => {
    getFullPbsData({ pbsCode })
  }

  const resolvedData = fullPbsData ?? pbsData

  return (
    <Card padding={3}>
      <VStack alignItems="stretch" key={`${resolvedData.pbsCode}_${resolvedData.pbsType}`}>
        <HStack>
          <Text fontWeight="bold">PBS Code:</Text>
          <Text flexGrow="1">{resolvedData.pbsCode}</Text>
          {handleSelect && !resolvedData.restrictions.find((r) => r.streamlinedCode) && (
            <VisuallyDisabledButton
              isVisuallyDisabled={isDisableS8Medicine || isOnlyPrivateMedicationAllowed}
              onClick={() => {
                if (isDisableS8Medicine) {
                  toast({
                    title: 'Warning',
                    description: 'Selection of S8 controlled substance is prohibited.',
                    status: 'warning',
                    duration: null,
                    isClosable: true,
                    position: 'top',
                  })
                  return
                }
                if (isOnlyPrivateMedicationAllowed) {
                  toast({
                    title: 'Warning',
                    description: `No Medicare or DVA number detected for ${selectedPatient.given_names} ${selectedPatient.family_name}. Selection of non-private medications is disabled.`,
                    status: 'warning',
                    duration: null,
                    isClosable: true,
                    position: 'top',
                  })
                  return
                }
                handleSelect(pbsData)
              }}
            >
              Select
            </VisuallyDisabledButton>
          )}
        </HStack>
        <HStack>
          <Text fontWeight="bold">PBS Type:</Text>
          <Text>{resolvedData.pbsType}</Text>
        </HStack>
        {resolvedData.pbsProgram ? (
          <HStack>
            <Text fontWeight="bold">PBS Program:</Text>
            <Text>{describePbsProgram(resolvedData.pbsProgram)}</Text>
          </HStack>
        ) : null}
        <HStack>
          <Text fontWeight="bold">Max Quantity:</Text>
          <Text>{resolvedData.maxQtyUnits}</Text>
        </HStack>
        <HStack>
          <Text fontWeight="bold">Max Repeats:</Text>
          <Text>{resolvedData.repeats}</Text>
        </HStack>
        <HStack>
          <Text fontWeight="bold">PBS Manufacturer Code:</Text>
          <Text>{resolvedData.pbsManufacturerCodes?.join()}</Text>
        </HStack>
        {resolvedData.restrictions.map((restriction: PbsRestriction, idx: number) => (
          <RestrictionRenderer
            key={idx}
            pbsData={resolvedData}
            restriction={restriction}
            handleSelect={handleSelect}
            handleReadMore={handleReadMore}
            disableSelect={isDisableS8Medicine || isOnlyPrivateMedicationAllowed}
          />
        ))}
      </VStack>
    </Card>
  )
}

interface RestrictionRendererProps {
  pbsData: PbsConcise
  restriction: PbsRestriction
  handleSelect?: (pbsData: PbsConcise, restriction?: PbsRestriction) => any
  handleReadMore: (pbsCode: string) => any
  disableSelect: boolean
}

const RestrictionRenderer: FunctionComponent<RestrictionRendererProps> = ({
  restriction,
  pbsData,
  handleSelect,
  handleReadMore,
  disableSelect,
}) => {
  const [isReadMoreClicked, setIsReadMoreClicked] = useState(false)
  const hasRestrictionText = (restriction.restrictionPreview?.split(' ').length || 0) >= 15
  const isShowReadMoreButton =
    (!isReadMoreClicked && hasRestrictionText) ||
    (isReadMoreClicked && !restriction.restrictionText)
  const description =
    (!isShowReadMoreButton ? restriction.restrictionText : restriction.restrictionPreview) ?? ''
  const handleClickReadMore = useCallback(() => {
    handleReadMore(pbsData.pbsCode)
    setIsReadMoreClicked(true)
  }, [])

  if (restriction.streamlinedCode) {
    return (
      <Card padding={3}>
        <VStack alignItems="stretch">
          <HStack>
            <Text fontWeight="bold">Streamline Code:</Text>
            <Text flex="1 1">{restriction.streamlinedCode}</Text>
            {handleSelect && (
              <Button
                colorScheme="red"
                size="sm"
                disabled={disableSelect}
                onClick={() => {
                  handleSelect(pbsData, restriction)
                }}
              >
                Select
              </Button>
            )}
          </HStack>
          <HStack>
            <Text fontWeight="bold">Restriction Type:</Text>
            <Text flex="1 1">{convertRestrictionType(restriction.restrictionType)}</Text>
          </HStack>
          <Divider />
          <ScrollableContainer maxHeight="400px" height="auto">
            <Text>{parse(description)}</Text>
          </ScrollableContainer>
          {isShowReadMoreButton && (
            <Button variant="link" onClick={handleClickReadMore}>
              Read more...
            </Button>
          )}
        </VStack>
      </Card>
    )
  } else if (description) {
    return (
      <>
        <Card padding="12px">
          <HStack>
            <Text fontWeight="bold">Restriction Type:</Text>
            <Text flex="1 1">{convertRestrictionType(restriction.restrictionType)}</Text>
          </HStack>
          <Divider />
          <ScrollableContainer maxHeight="400px" height="auto">
            <Text>{parse(description)}</Text>
          </ScrollableContainer>
          {isShowReadMoreButton && (
            <Button variant="link" onClick={handleClickReadMore}>
              Read more...
            </Button>
          )}
        </Card>
      </>
    )
  }
  return <></>
}

export default AuthorityDetailsPanel
